import { Component, Show, createSignal, For, onMount, onCleanup, createEffect, createMemo } from 'solid-js';
import { Portal } from 'solid-js/web';
import { A } from '@solidjs/router';
import type { NostrEvent } from 'nostr-tools/core';
import { getPowDifficulty, hasValidPow, formatPowDifficulty } from '../lib/pow';
import { computeRelayScore } from '../lib/scoring';
import { nip19 } from 'nostr-tools';
import { ReactionPicker } from './ReactionPicker';
import { ReplyComposer } from './ReplyComposer';
import { ReportModal } from './ReportModal';
import { RepostConfirmDialog } from './RepostConfirmDialog';
import { ProfileName } from './ProfileName';
import { FollowButton } from './FollowButton';
import { ParsedContent } from './ParsedContent';
import { ReactionBreakdown } from './ReactionBreakdown';
import { usePreferences } from '../providers/PreferencesProvider';
import { useTooltip } from '../providers/TooltipProvider';
import { useUser } from '../providers/UserProvider';
import { useQueue } from '../providers/QueueProvider';
import type { PreparedNote } from '../types/FeedTypes';
import { getVisibilityObserver } from '../services/VisibilityObserver';
import { getInteractionsCoordinator } from '../services/InteractionsCoordinator';
import { buildRelayHintsForEvent } from '../lib/relayHints';
import { getEventRelayHint, addRelayHintToETag } from '../lib/inbox-outbox';
import { extractTopicsFromEvent } from '../lib/content-parser';

interface NoteProps {
  event: NostrEvent;
  score?: number;
  showScore?: boolean;
  reactions?: NostrEvent[];
  replies?: NostrEvent[];
  onVisible?: (eventId: string) => void; // Callback when note becomes visible
  preparedNote?: PreparedNote; // Reserved heights for stable rendering (Phase 2)
  interactionTick?: number; // Forces reactive updates on interaction arrivals
  isHydrated?: boolean;
}

// Global signal to track which note's tooltip is open (must be reactive)
import { createSignal as createGlobalSignal } from 'solid-js';
const [globalOpenTooltipId, setGlobalOpenTooltipId] = createGlobalSignal<string | null>(null);

export const Note: Component<NoteProps> = (props) => {
  const [showReactionPicker, setShowReactionPicker] = createSignal(false);
  const [showReplyComposer, setShowReplyComposer] = createSignal(false);
  const [showReportModal, setShowReportModal] = createSignal(false);
  const [showRepostDialog, setShowRepostDialog] = createSignal(false);
  const [showScoreTooltip, setShowScoreTooltip] = createSignal(false);
  const { preferences } = usePreferences();
  const { activeTooltip, setActiveTooltip, setTooltipContent, closeAllPanels } = useTooltip();
  const { user } = useUser();
  const { addToQueue } = useQueue();
  const [isExpanded, setIsExpanded] = createSignal(false);
  const [contentOverflows, setContentOverflows] = createSignal(false);
  const [isHovered, setIsHovered] = createSignal(false);
  const [boostQueued, setBoostQueued] = createSignal(false);

  // Ref for intersection observer
  let noteRef: HTMLDivElement | undefined;
  let scoreSpanRef: HTMLSpanElement | undefined;
  let contentRef: HTMLDivElement | undefined;

  const powDifficulty = () => getPowDifficulty(props.event);
  const hasPow = () => hasValidPow(props.event, 1);
  const formattedPow = () => formatPowDifficulty(powDifficulty());

  // Calculate stats directly from props (single source of truth with Timeline)
  const reactionsList = createMemo(() => {
    // Depend on interactionTick so updates stream in immediately
    void (props.interactionTick ?? 0);
    return props.reactions || [];
  });

  const repliesList = createMemo(() => {
    void (props.interactionTick ?? 0);
    return props.replies || [];
  });

  const stats = createMemo(() => {
    const prefs = preferences();
    const reactions = reactionsList();
    const replies = repliesList();
    const reactionsPow = reactions.reduce((sum, r) => sum + getPowDifficulty(r), 0);
    const repliesPow = replies.reduce((sum, r) => sum + getPowDifficulty(r), 0);
    return computeRelayScore({
      base: getPowDifficulty(props.event),
      pos: reactionsPow,
      neg: 0,
      report: 0,
      replyPow: repliesPow,
      repostPow: 0,
      replyCount: replies.length,
      reactionCount: reactions.length,
    }, {
      replyWeight: prefs.replyWeight,
      repostWeight: prefs.repostWeight,
      replyCountWeight: prefs.replyCountWeight,
      subtractNegative: prefs.subtractNegative,
      subtractReports: prefs.subtractReports,
      reportWeight: prefs.reportWeight,
      decayLambda: prefs.decayLambda,
    }, 0);
  });

  const interactionSummaryReady = createMemo(() => {
    const delegated = stats().pos + stats().replyPow;
    return reactionsList().length > 0 || repliesList().length > 0 || delegated > 0;
  });

  const hasReactionPills = createMemo(() => reactionsList().length > 0);
  const relayHints = createMemo(() => buildRelayHintsForEvent(props.event));

  const topicData = createMemo(() => extractTopicsFromEvent(props.event));

  const noteContent = () => topicData().sanitizedContent;
  const topics = () => topicData().topics;
  const viewOptions = () => preferences().feedView;
  const maxNoteHeight = () => Math.max(0, viewOptions()?.maxNoteHeightPx ?? 0);
  const showInteractionDetails = () => viewOptions()?.showInteractionCounts ?? true;
  const clampActive = () => maxNoteHeight() > 0 && !isExpanded() && contentOverflows();

  const scheduleOverflowCheck = () => {
    if (!contentRef) return;
    requestAnimationFrame(() => {
      if (!contentRef) return;
      if (maxNoteHeight() <= 0) {
        setContentOverflows(false);
        return;
      }
      setContentOverflows(contentRef.scrollHeight > maxNoteHeight());
    });
  };

  const contentClass = () =>
    [
      'leading-relaxed text-xl',
      !hasPow() ? 'opacity-70' : null,
    ]
      .filter(Boolean)
      .join(' ');

  const hydrated = () => props.isHydrated ?? true;

  // Unique ID for this note's tooltip
  const tooltipId = `tooltip-${props.event.id}`;

  // Watch for global tooltip changes and close this one if another opens
  createEffect(() => {
    if (globalOpenTooltipId() !== tooltipId && showScoreTooltip()) {
      setShowScoreTooltip(false);
    }
  });

  // Removed shortPubkey - now using ProfileName component

  const scoreBreakdown = () => {
    const s = stats();
    const prefs = preferences();

    // Build the calculation breakdown using relay-aligned components
    const lines = [
      'Score Breakdown:',
      '',
      `Base POW: ${s.base}`,
    ];

    if (s.pos > 0) {
      lines.push(`Reactions POW: ${s.pos.toFixed(1)}`);
    }

    if (s.replyPow > 0) {
      lines.push(`Reply POW: ${s.replyPow.toFixed(1)} x ${prefs.replyWeight} = ${(s.replyPow * prefs.replyWeight).toFixed(1)}`);
    }

    if (s.replyCount > 0) {
      lines.push(`Reply Count: ${s.replyCount} x ${prefs.replyCountWeight} = ${(s.replyCount * prefs.replyCountWeight).toFixed(1)}`);
    }

    if (s.reactionCount > 0) {
      lines.push(`Reactions: ${s.reactionCount}`);
    }

    lines.push('');
    lines.push(`Raw Total = ${s.rawTotal.toFixed(1)}`);

    return lines.join('\n');
  };

  const toggleTooltip = () => {
    if (activeTooltip() === tooltipId) {
      setActiveTooltip(null);
    } else {
      // Close any open panels first
      closeAllPanels?.();
      setTooltipContent(scoreBreakdown());
      setActiveTooltip(tooltipId);
    }
  };

  const timestamp = () => {
    const date = new Date(props.event.created_at * 1000);
    const now = new Date();
    const diff = now.getTime() - date.getTime();

    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return `${seconds}s ago`;
  };

  // Extract topic tags (#t tags)
  const noteLink = () => {
    const nevent = nip19.neventEncode({
      id: props.event.id,
      author: props.event.pubkey,
      relays: relayHints() || undefined,
    });
    return `/e/${nevent}`;
  };

  const handleBoost = () => {
    const currentUser = user();
    if (!currentUser || currentUser.isAnon) return;
    addToQueue({
      type: 'reaction',
      content: '+',
      pubkey: currentUser.pubkey,
      difficulty: preferences().powDifficultyReaction,
      tags: [
        addRelayHintToETag(props.event.id, getEventRelayHint(props.event.id)),
        ['p', props.event.pubkey],
        ['client', 'notemine.io'],
      ],
      kind: 7,
      metadata: {
        targetEventId: props.event.id,
        targetAuthor: props.event.pubkey,
        reactionContent: '+',
      },
    });
    setBoostQueued(true);
    setTimeout(() => setBoostQueued(false), 2000);
  };

  createEffect(() => {
    const height = maxNoteHeight();
    if (height <= 0) {
      setIsExpanded(false);
      setContentOverflows(false);
      return;
    }
    setIsExpanded(false);
    scheduleOverflowCheck();
  });

  createEffect(() => {
    void props.interactionTick;
    scheduleOverflowCheck();
  });

  createEffect(() => {
    void props.event.id;
    setIsExpanded(false);
    scheduleOverflowCheck();
  });

  // Phase 2: Set up global visibility observer for lazy loading with dwell time
  onMount(() => {
    scheduleOverflowCheck();
    const { preferences } = usePreferences();

    if (noteRef && props.onVisible) {
      const visibilityObserver = getVisibilityObserver();
      getInteractionsCoordinator();
      let cancelTimer: number | null = null;

      const registerWithObserver = () => {
        // Ensure prior registration is cleared before re-registering
        visibilityObserver.unregister(noteRef!);
        visibilityObserver.register({
          element: noteRef!,
          onVisible: () => {
            // Clear any pending cancel and trigger fetch (coordinator dedupes internally)
            if (cancelTimer !== null) {
              clearTimeout(cancelTimer);
              cancelTimer = null;
            }
            props.onVisible?.(props.event.id);
          },
          onLeave: () => {
            // Do not cancel queued interactions on leave; allow queue to proceed
            // This prevents starvation where only the first N (maxConcurrent) notes ever load
            if (cancelTimer !== null) {
              clearTimeout(cancelTimer);
              cancelTimer = null;
            }
          },
        });
      };

      // Initial registration
      let prevRegisteredEventId = props.event.id;
      registerWithObserver();

      // Phase 9 Fix 4: Re-register only if this DOM node is reused for a DIFFERENT event
      createEffect(() => {
        const currentId = props.event.id;
        if (currentId === prevRegisteredEventId) return;
        prevRegisteredEventId = currentId;
        registerWithObserver();
      });

      onCleanup(() => {
        visibilityObserver.unregister(noteRef!);
        // Do not cancel in-flight fetches on cleanup; allow them to complete
        if (cancelTimer !== null) {
          clearTimeout(cancelTimer);
          cancelTimer = null;
        }
      });
    }

    // Phase 2: ResizeObserver to assert heights don't shrink (stable rendering)
    // Only enabled when feedDebugMode is on
    if (noteRef && props.preparedNote && preferences().feedDebugMode) {
      let maxHeight = 0;
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const currentHeight = entry.contentRect.height;

          // Initialize max height on first observation
          if (maxHeight === 0) {
            maxHeight = currentHeight;
            return;
          }

          // Check if height decreased (potential layout shift)
          if (currentHeight < maxHeight) {
            const shrinkage = maxHeight - currentHeight;
            console.warn(
              `[Note ${props.event.id.slice(0, 8)}] Height decreased by ${shrinkage.toFixed(1)}px (${maxHeight.toFixed(1)}px -> ${currentHeight.toFixed(1)}px). This may cause layout shifts.`
            );
            // Update max to allow for intentional height changes (like collapsed content)
            // but we've logged the warning
          } else if (currentHeight > maxHeight) {
            // Height increased - this is acceptable (content expanding)
            maxHeight = currentHeight;
          }
        }
      });

      resizeObserver.observe(noteRef);

      onCleanup(() => resizeObserver.disconnect());
    }
  });

  return (
    <div
      ref={noteRef}
      data-note-id={props.event.id}
      data-interaction-tick={props.interactionTick ?? 0}
      class="!mb-10 note relative group"
      classList={{
        'border-l-accent bg-bg-primary dark:bg-bg-secondary': hasPow(),
        'border-l-gray-500/30 bg-bg-secondary dark:bg-bg-tertiary': !hasPow(),
      }}
      onPointerEnter={() => setIsHovered(true)}
      onPointerLeave={() => setIsHovered(false)}
      onPointerCancel={() => setIsHovered(false)}
    >
      {/* Header - Low contrast metadata */}
      <div class="flex items-start justify-between mb-2">
        <div class="flex items-center gap-2 min-w-0 flex-1 opacity-60">
          <ProfileName pubkey={props.event.pubkey} asLink={true} showAvatar={true} />
          <FollowButton
            pubkey={props.event.pubkey}
            size="xs"
            variant="ghost"
            class={`transition-opacity duration-150 ${
              isHovered() ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
            }`}
          />
          <div class="ml-auto text-xs text-text-tertiary pr-2">{timestamp()}</div>
        </div>

        <div class="flex items-center gap-2">
          {/* POW Badge */}
          <Show when={hasPow()} fallback={<span class="text-xs text-text-tertiary opacity-60">no pow</span>}>
            <span
              class="text-xs font-mono text-accent/70 cursor-pointer hover:text-accent transition-colors"
              onClick={toggleTooltip}
            >
              {formattedPow()}
            </span>
          </Show>

          {/* Score (if provided) */}
          <Show when={props.showScore && props.score !== undefined}>
            <div class="relative">
              <span
                ref={scoreSpanRef}
                class="text-xs font-mono text-text-tertiary cursor-pointer hover:text-text-secondary transition-colors opacity-60"
                onClick={() => {
                  const currentlyOpen = showScoreTooltip();

                  // Toggle this tooltip
                  if (currentlyOpen) {
                    setShowScoreTooltip(false);
                    setGlobalOpenTooltipId(null);
                  } else {
                    setShowScoreTooltip(true);
                    setGlobalOpenTooltipId(tooltipId);
                  }
                }}
              >
                {props.score?.toFixed(1)}
              </span>

              {/* Tooltip positioned relative to score - stays pinned */}
              <Show when={showScoreTooltip()}>
                <div
                  class="
                    absolute 
                    top-6 
                    right-0 
                    z-[9998] 
                    opacity-100
                    tooltip
                    min-w-[300px] 
                    max-w-[400px]"
                  onClick={(e) => e.stopPropagation()}
                >
                  <pre class="text-xs font-mono text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                    {scoreBreakdown()}
                  </pre>
                </div>
              </Show>
            </div>
          </Show>
        </div>
      </div>

      <div class="my-6 relative">
        {/* Content - HIGH CONTRAST, the focus */}
        <div
          ref={contentRef}
          style={
            maxNoteHeight() > 0 && !isExpanded()
              ? { 'max-height': `${maxNoteHeight()}px`, overflow: 'hidden' }
              : undefined
          }
        >
          <ParsedContent
            content={noteContent()}
            event={props.event}
            class={contentClass()}
            reservedHeights={props.preparedNote?.reservedHeights}
          />
        </div>

        <Show when={clampActive()}>
          <div class="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-[var(--bg-primary)] dark:to-[var(--bg-secondary)]" />
          <div class="absolute bottom-4 left-0 right-0 flex justify-center">
            <button
              type="button"
              class="px-4 py-1 text-xs font-semibold border border-[var(--border-color)] rounded bg-[var(--bg-primary)]/80 backdrop-blur hover:border-[var(--accent)] transition-colors"
              onClick={() => {
                setIsExpanded(true);
                requestAnimationFrame(() => setContentOverflows(false));
              }}
            >
              Expand note
            </button>
          </div>
        </Show>
      </div>

      {/* Topics - Display #t tags */}
      <Show when={topics().length > 0}>
        <div class="flex flex-wrap gap-2 mb-3">
          <For each={topics()}>
            {(topic) => (
              <span class="text-xs px-2 py-1 rounded-md bg-accent/10 text-accent/80 hover:bg-accent/20 transition-colors">
                #{topic}
              </span>
            )}
          </For>
        </div>
      </Show>

      

      <Show when={showInteractionDetails()}>
        <div class="mb-3 text-xs font-mono text-black/60 dark:text-white/60 min-h-[24px] flex items-center">
          <Show when={interactionSummaryReady()}>
            <div>
              <Show when={stats().pos + stats().replyPow > 0}>
                <span
                  class="text-text-secondary cursor-help"
                  title="Contributed Work: Total raw POW from reactions and replies combined"
                >
                  💎 <strong class="text-white">{(stats().pos + stats().replyPow).toFixed(1)} work delegated</strong> via{' '}
                </span>
              </Show>
              <Show when={repliesList().length > 0}>
                {repliesList().length} replies
              </Show>
              <Show when={repliesList().length > 0 && reactionsList().length > 0}>
                {' & '}
              </Show>
              <Show when={reactionsList().length > 0}>
                {reactionsList().length} reactions
              </Show>
            </div>
          </Show>
          <Show when={!interactionSummaryReady() && !hydrated()}>
            <div class="h-4 w-32" />
          </Show>
        </div>
      </Show>



      {/* Reactions Bar - Visual breakdown of reactions */}
      <Show when={showInteractionDetails()}>
        <div class="mb-3 min-h-[32px]">
          <Show when={hasReactionPills()}>
            <ReactionBreakdown
              reactions={reactionsList()}
              eventId={props.event.id}
              eventAuthor={props.event.pubkey}
            />
          </Show>
          <Show when={!hasReactionPills() && !hydrated()}>
            <div class="h-7" />
          </Show>
        </div>
      </Show>

      
      
      {/* Interaction Stats - removed during relay-aligned scoring migration */}

      {/* Footer - Low contrast actions */}
      <div class="flex gap-4 text-xs opacity-50 hover:opacity-70 transition-opacity">
        <A
          href={noteLink()}
          class="text-text-tertiary hover:text-accent transition-colors"
        >
          view
        </A>
        <button
          onClick={() => setShowReplyComposer(true)}
          class="text-text-tertiary hover:text-accent transition-colors"
        >
          reply
        </button>
        <button
          onClick={handleBoost}
          disabled={!user() || user()?.isAnon || boostQueued()}
          class="text-text-tertiary hover:text-accent transition-colors"
          classList={{ 'text-accent': boostQueued() }}
          title={!user() || user()?.isAnon ? 'Login to boost' : 'Boost with POW'}
        >
          {boostQueued() ? 'boosted' : 'boost'}
        </button>
        <button
          onClick={() => setShowReactionPicker(true)}
          class="text-text-tertiary hover:text-accent transition-colors"
        >
          react
        </button>
        <button
          onClick={() => setShowRepostDialog(true)}
          class="text-text-tertiary hover:text-green-500 transition-colors"
        >
          repost
        </button>
        <button
          onClick={() => {
            const noteId = nip19.noteEncode(props.event.id);
            navigator.clipboard.writeText(`https://notemine.io/n/${noteId}`);
          }}
          class="text-text-tertiary hover:text-accent transition-colors"
        >
          share
        </button>
        <button
          onClick={() => setShowReportModal(true)}
          class="text-text-tertiary hover:text-red-500 transition-colors"
        >
          report
        </button>
      </div>

      {/* Reaction Picker Modal - Rendered at document root */}
      <Portal>
        <Show when={showReactionPicker()}>
          <ReactionPicker
            eventId={props.event.id}
            eventAuthor={props.event.pubkey}
            onClose={() => setShowReactionPicker(false)}
          />
        </Show>
      </Portal>

      {/* Reply Composer Modal - Rendered at document root */}
      <Portal>
        <Show when={showReplyComposer()}>
          <ReplyComposer
            parentEvent={props.event}
            onClose={() => setShowReplyComposer(false)}
          />
        </Show>
      </Portal>

      {/* Report Modal - Rendered at document root */}
      <Portal>
        <Show when={showReportModal()}>
          <ReportModal
            isOpen={showReportModal()}
            onClose={() => setShowReportModal(false)}
            eventId={props.event.id}
            authorPubkey={props.event.pubkey}
            targetKind={1}
          />
        </Show>
      </Portal>

      {/* Repost Dialog - Rendered at document root */}
      <Portal>
        <Show when={showRepostDialog()}>
          <RepostConfirmDialog
            isOpen={showRepostDialog()}
            onClose={() => setShowRepostDialog(false)}
            originalEvent={props.event}
          />
        </Show>
      </Portal>
    </div>
  );
};
